/**
 * The EU taxonomy figures.
 *
 * Everything this kind is: how it is made, how it is read back, what it looks
 * like on the page, and how it is edited. Nothing outside this folder needs to
 * know the kind exists.
 */

import { readArray, readString } from '@/domain/json'
import type { JsonValue } from '@/domain/json'
import { type TaxonomySection } from '@/domain/section'
import {
  newTaxonomyKpis,
  TAXONOMY_KPIS,
  type TaxonomyKpi,
  type TaxonomyKpiId,
} from '@/domain/taxonomy'
import TaxonomyBlock from '@/sections/taxonomy/TaxonomyBlock.vue'
import TaxonomyInspector from '@/sections/taxonomy/TaxonomyInspector.vue'
import type { SectionDefinition } from '@/sections/types'

function readKpi(value: JsonValue): TaxonomyKpi | null {
  const id = readString(value, 'id')
  if (!(TAXONOMY_KPIS as readonly string[]).includes(id)) return null

  return {
    id: id as TaxonomyKpiId,
    total: readString(value, 'total'),
    eligible: readString(value, 'eligible'),
    aligned: readString(value, 'aligned'),
  }
}

/**
 * The three, read back.
 *
 * Any that were not stored come back empty rather than missing: the regulation
 * asks for three whatever a particular document happens to hold, and a table
 * with one of them silently absent is a table nobody can tell is incomplete.
 */
function readKpis(stored: JsonValue): TaxonomyKpi[] {
  const found = readArray(stored, 'kpis')
    .map(readKpi)
    .filter((kpi): kpi is TaxonomyKpi => kpi !== null)

  return newTaxonomyKpis().map((blank) => found.find((kpi) => kpi.id === blank.id) ?? blank)
}

export const taxonomySection: SectionDefinition<'taxonomy', TaxonomySection> = {
  kind: 'taxonomy',
  band: 'sustainability',

  icon: ['M2.5 13.5h11', 'M4 13.5V9', 'M8 13.5V5.5', 'M12 13.5V7', 'M2.5 3h4'],

  create: (id, title) => ({
    id,
    kind: 'taxonomy',
    title,
    kpis: newTaxonomyKpis(),
    unit: '',
    period: '',
    caption: '',
  }),

  read: ({ id, title, stored }) => ({
    id,
    kind: 'taxonomy',
    title,
    kpis: readKpis(stored),
    unit: readString(stored, 'unit'),
    period: readString(stored, 'period'),
    caption: readString(stored, 'caption'),
  }),

  block: TaxonomyBlock,
  inspector: TaxonomyInspector,
}
